import React, { createContext, useCallback, useEffect, useState } from 'react';
import { Device, Call } from '@twilio/voice-sdk';
import { TwilioError } from 'twilio-video';
import { logger, isElectron } from '../../utils';
import { useTwilioTokenContext } from './useTwilioTokenContext';

export const AudioDeviceContext = createContext<{
  device: Device | null;
  activeCall: Call | null;
  resumeAudioAndRegister: () => Promise<void>;
}>({
  device: null,
  activeCall: null,
  resumeAudioAndRegister: async () => {},
});

export function AudioDeviceContextProvider({ children }: React.PropsWithChildren<{}>): JSX.Element {
  const { token, loadTwilioToken } = useTwilioTokenContext();
  const [device, setDevice] = useState<Device | null>(null);
  const [activeCall, setActiveCall] = useState<Call | null>(null);

  const buildDevice = useCallback(
    (authToken: string) => {
      logger({ message: 'Initializing Twilio Device...' });
      const newDevice = new Device(authToken, {
        sounds: { incoming: 'https://audiocalltest.s3.us-east-2.amazonaws.com/video_reciever_tone.mp3' },
      });

      const handleRegistered = () => {
        logger({ message: 'Twilio Device is now registered (ready for connection)' });
      };
      const handleRegistering = () => {
        logger({ message: 'Twilio Device is registering...' });
      };
      const handleUnregistered = () => {
        logger({ message: 'Twilio Device is now unregistered' });
      };
      const handleError = (error: TwilioError) => {
        logger({ message: 'Twilio Device Error', data: error.message });
      };

      newDevice.on('registered', handleRegistered);
      newDevice.on('registering', handleRegistering);
      newDevice.on('unregistered', handleUnregistered);
      newDevice.on('error', handleError);

      newDevice.register().catch((error) => {
        logger({ message: 'Device registration failed', data: error.message });
      });

      setDevice(newDevice);

      newDevice.on('incoming', (call) => {
        logger({ message: 'Incoming call received', data: call });
        setActiveCall(call);
      });

      return () => {
        newDevice.off('registered', handleRegistered);
        newDevice.off('registering', handleRegistering);
        newDevice.off('unregistered', handleUnregistered);
        newDevice.off('error', handleError);
        newDevice.destroy();
        setDevice(null);
      };
    },
    []
  );

  useEffect(() => {
    console.log('[AudioDeviceContext] Token:', token);
    if (!token) return;

    const cleanup = buildDevice(token);
    return () => {
      cleanup && cleanup();
    };
  }, [token, buildDevice]);

  useEffect(() => {
    const handleDeviceOffline = (error: TwilioError) => {
      if (error.code === 31205) {
        loadTwilioToken();
      }
    };
    device?.on('error', handleDeviceOffline);

    return () => {
      device?.off('error', handleDeviceOffline);
    };
  }, [loadTwilioToken, device]);

  const resumeAudioAndRegister = useCallback(async () => {
    if (isElectron()) return;

    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtx) {
        const ctx = new AudioCtx();
        if (ctx.state === 'suspended') {
          await ctx.resume();
        }
        ctx.close?.();
      }
    } catch (err) {
      logger({ message: 'AudioContext resume failed', data: (err as Error).message });
    }

    if (!token) {
      await loadTwilioToken();
      return;
    }

    if (device?.state === Device.State.Registered) {
      return;
    }

    if (device) {
      try {
        await device.register();
        return;
      } catch (err) {
        logger({ message: 'Re-register after gesture failed; rebuilding device', data: (err as Error).message });
        device.destroy();
        setDevice(null);
      }
    }

    buildDevice(token);
  }, [device, token, loadTwilioToken, buildDevice]);

  return (
    <AudioDeviceContext.Provider value={{ device, activeCall, resumeAudioAndRegister }}>
      {children}
    </AudioDeviceContext.Provider>
  );
}
